import axios from "axios";
import { HotType } from "../models/hotType";
import { SportType } from "../models/sportType";
import {
    PeriodData,
    SkillStats,
    StatsUserInfo,
    UserStatsApiResponse,
    UserStatsData,
    WeeklyHistoricalRating
} from "../models/userStats";

// ==================== HELPER FUNCTIONS ====================

/**
 * Map HotType to the API's period key
 */
const mapHotTypeToPeriodKey = (period: HotType): keyof import("../models/userStats").PeriodsData => {
    switch (period) {
        case HotType.Week:
            return 'weekly';
        case HotType.Month:
            return 'monthly';
        case HotType.Season:
            return 'seasonal';
        case HotType.Year:
            return 'yearly';
        case HotType.All:
        case HotType.Day:
        default:
            return 'all_time';
    }
};

/**
 * Map SportType to the API's epic_name
 */
const mapSportTypeToEpicName = (sport: SportType): string => {
    switch (sport) {
        case SportType.SKIING:
            return 'Skiing';
        case SportType.SNOWBOARDING:
            return 'Snowboarding';
        default:
            return 'Skiing';
    }
};

/**
 * Transform feature stats from API response to SkillStats object
 */
const transformFeaturesToSkillStats = (periodData: PeriodData): SkillStats => {
    const stats: SkillStats = {
        rail: 0,
        jump: 0,
        style: 0,
        off_snow: 0,
        mountain: 0,
        halfpipe: 0
    };

    for (const feature of periodData.features) {
        switch (feature.feature_name) {
            case 'Rail/Jib':
                stats.rail = feature.display_score;
                break;
            case 'Jump':
                stats.jump = feature.display_score;
                break;
            case 'Style':
                stats.style = feature.display_score;
                break;
            case 'Off-Snow':
                stats.off_snow = feature.display_score;
                break;
            case 'Freeride':
                stats.mountain = feature.display_score;
                break;
            case 'Halfpipe':
                stats.halfpipe = feature.display_score;
                break;
        }
    }

    return stats;
};

/**
 * Transform API response to UserStatsData format
 */
const transformApiResponse = (
    apiResponse: UserStatsApiResponse,
    period: HotType,
    sport: SportType,
    username?: string,
    profilePic?: string
): UserStatsData | null => {
    if (!apiResponse || !apiResponse.epics || apiResponse.epics.length === 0) {
        return null;
    }

    // Find the epic for the requested sport
    const epicName = mapSportTypeToEpicName(sport);
    const epic = apiResponse.epics.find(e => e.epic_name === epicName);

    if (!epic) {
        console.warn(`No epic found for sport: ${sport}, available epics:`, apiResponse.epics.map(e => e.epic_name));
        // If only one epic exists, use it (as user mentioned "assume it only contains one sport")
        if (apiResponse.epics.length === 1) {
            const singleEpic = apiResponse.epics[0];
            const periodKey = mapHotTypeToPeriodKey(period);
            const periodData = singleEpic.periods[periodKey];

            if (!periodData) {
                return null;
            }

            return {
                user_id: apiResponse.user_id,
                username: username || '',
                profile_pic: profilePic,
                period: period,
                sport: sport,
                stats: transformFeaturesToSkillStats(periodData),
                last_updated: new Date().toISOString()
            };
        }
        return null;
    }

    const periodKey = mapHotTypeToPeriodKey(period);
    const periodData = epic.periods[periodKey];

    if (!periodData) {
        return null;
    }

    return {
        user_id: apiResponse.user_id,
        username: username || '',
        profile_pic: profilePic,
        period: period,
        sport: sport,
        stats: transformFeaturesToSkillStats(periodData),
        last_updated: new Date().toISOString()
    };
};

// ==================== END HELPER FUNCTIONS ====================

// Export helper functions for client-side transformation
export { mapHotTypeToPeriodKey, mapSportTypeToEpicName, transformApiResponse, transformFeaturesToSkillStats };


const StatsService = {
    /**
     * Get raw stats data for a user - returns all periods and sports in one response
     * Use this with React Query to cache the full response and transform client-side
     */
    getUserStatsRaw: async (user_id: string): Promise<UserStatsApiResponse | null> => {
        const params = {
            user_id: user_id
        };

        return await axios.get(`${process.env.REACT_APP_MCTWIST_USER_SERVER}/stats/user`, { params: params })
            .then((response) => {
                return response.data as UserStatsApiResponse;
            }).catch((error): null => {
                console.error('Error fetching user stats:', error);
                return null;
            });
    },



    /**
     * Get raw stats for multiple comparison users
     * Returns a map of user_id -> UserStatsApiResponse for caching
     */
    getComparisonUsersStatsRaw: async (user_ids: string[]): Promise<Map<string, UserStatsApiResponse>> => {
        const results = new Map<string, UserStatsApiResponse>();

        const promises = user_ids.map(user_id =>
            axios.get(`${process.env.REACT_APP_MCTWIST_USER_SERVER}/stats/user`, { params: { user_id } })
                .then(response => {
                    results.set(user_id, response.data as UserStatsApiResponse);
                })
                .catch(error => {
                    console.error(`Error fetching stats for user ${user_id}:`, error);
                })
        );

        await Promise.all(promises);
        return results;
    },

    /**
     * Get suggested users for comparison (e.g., followers, following, nearby users)
     * Optionally filter by sport
     */
    getSuggestedComparisonUsers: async (user_id: string, sport?: SportType, limit: number = 5): Promise<StatsUserInfo[]> => {
        const params: any = {
            user_id: user_id,
            limit: limit
        };

        if (sport) {
            params.sport = sport;
        }

        return await axios.get(`${process.env.REACT_APP_MCTWIST_USER_SERVER}/stats/suggested/users`, { params: params })
            .then((response) => {
                return response.data;
            }).catch((error): StatsUserInfo[] => {
                console.error('Error fetching suggested users:', error);
                return [];
            });
    },

    /**
     * Get weekly historical ratings for the user
     * Returns an array of weekly ratings for the overall score
     * The backend determines how many weeks to return
     * Returns empty array if API returns empty (triggers "Coming Soon" in UI)
     */
    getWeeklyHistoricalRatings: async (user_id: string, sport: SportType): Promise<WeeklyHistoricalRating[]> => {
        const params = {
            user_id: user_id,
            sport: sport
        };

        return await axios.get(`${process.env.REACT_APP_MCTWIST_USER_SERVER}/stats/weekly-history`, { params: params })
            .then((response) => {
                // Return the data as-is - empty array will trigger "Coming Soon" in UI
                return response.data as WeeklyHistoricalRating[];
            }).catch((error): WeeklyHistoricalRating[] => {
                console.error('Error fetching weekly historical ratings:', error);
                // Return empty array to show "Coming Soon" state
                return [];
            });
    }
};

/**
 * Generate mock weekly data for the past 13 weeks
 * This provides fallback data when the API is unavailable
 */
const generateMockWeeklyData = (): WeeklyHistoricalRating[] => {
    const data: WeeklyHistoricalRating[] = [];
    const today = new Date();

    for (let i = 12; i >= 0; i--) {
        const weekDate = new Date(today);
        weekDate.setDate(weekDate.getDate() - (i * 7));

        // Generate a somewhat realistic progression with some variance
        const baseRating = 5.5;
        const trend = (12 - i) * 0.1; // Slight upward trend over time
        const variance = (Math.random() - 0.5) * 2; // Random variance between -1 and 1
        const rating = Math.min(10, Math.max(0, baseRating + trend + variance));

        data.push({
            week_start: weekDate.toISOString(),
            week_number: 13 - i,
            overall_rating: Number(rating.toFixed(1))
        });
    }

    return data;
};

export default StatsService;
